/** @jsxImportSource @emotion/react */

import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchActivePlan } from 'shared/hooks/fetchering/use-fetch-active-plan';

import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { UDText } from 'ud-ui/components';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { UpsertLocationInput } from 'modules/locations/domain/interfaces/actions';
import { fetchLocation, upsertLocation } from 'modules/locations/store/actions';
import { selectCurrentLocation } from 'modules/locations/store/selectors';
import LocationForm from '../components/location-form';
import useFetchMapImage from '../hooks/use-fetch-map-image';

function LocationViewPage() {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const navigate = useNavigate();
  const location = useSelector(selectCurrentLocation);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const { setHeaderContent } = useMainLayout();
  const { data: activePlan, isFetching: isPlanFetching } = useFetchActivePlan();

  const id = Number(useParams().id);
  if (isNaN(id)) {
    navigate(Pages.LOCATIONS.INDEX);
  }

  const { isLoading: isMapImageLoading } = useFetchMapImage({ locationId: id });

  // Карточка всегда читает локацию сама: подглядывать в перечень больше не во что —
  // список живёт в кэше RTK Query и выбирает лишь поля строки.
  useEffect(() => {
    setIsLocationLoading(true);
    dispatch(fetchLocation({ id })).finally(() => setIsLocationLoading(false));
  }, [dispatch, id]);

  const handleSubmit = useCallback(
    async (values: UpsertLocationInput) => {
      if (!tournamentId) {
        console.error('No tournament selected');
        return;
      }

      const locationData: UpsertLocationInput = {
        ...values,
        tournamentId,
      };

      await dispatchWithToast(upsertLocation(locationData), {
        success: 'Локация успешно сохранена',
        error: 'Не удалось сохранить локацию',
      });
    },
    [dispatchWithToast, tournamentId],
  );

  const breadcrumbs = useMemo(() => {
    return [
      { label: 'Локации', to: Pages.LOCATIONS.INDEX },
      {
        label: location?.name ?? 'Неизвестная локация',
        to: Pages.LOCATIONS.builders.edit(id),
      },
    ];
  }, [id, location?.name]);

  const breadcrumb = useMemo(() => <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbs} />, [breadcrumbs]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  if (isPlanFetching || isLocationLoading) {
    return (
      <>
        <div className="w-full px-6 pt-6">
          <div className="skeleton-block" style={{ height: '200px' }} />
        </div>
      </>
    );
  }

  if (!location) {
    return (
      <>
        <div className="w-full px-6 pt-6">
          <div className="flex-center mt-24 flex flex-col">
            <div className="text body-text bold mb-2">Такая локация не найдена</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full px-6 py-6">
        <UDText type="headline" className="mr-10">
          Локация для проведения турнира
        </UDText>
      </div>
      <div className="border-surfacePlatinum20 flex w-full items-center border-b px-6" />
      <div className="w-full px-6 pt-6">
        <LocationForm
          location={location}
          isLoading={false}
          onSubmit={handleSubmit}
          action="edit"
          isAvailableFeatures={Boolean(activePlan)}
          isMapImageLoading={isMapImageLoading}
        />
      </div>
    </>
  );
}

export default LocationViewPage;
